import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import type {
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowClickedEvent,
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { useTheme } from '@material-ui/core/styles';

import { AgGridTableWrapper } from '@mummssoftware/common-ui';
import type { PatientChatPatientSummary } from '@mummssoftware/utils';

import { useCostColumnsShown } from '../../hooks/useShowCost';
import defaults from '../../translations/en.json';
import { HISTORY_NUMERIC_CLASS } from './historyColumnDefs';
import { getPatientListColumnDefs } from './patientListColumnDefs';
import { LAST_TOKEN_COLUMN } from './tokenColumnDefs';
import {
  QUESTION_DATE_FILTER_HEIGHT,
  QuestionDateFilterContext,
  QuestionDateFilterState,
  questionDateFilterGridCss,
  withQuestionDateFilter,
} from './QuestionDateFilter';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/agGridAlpineFont.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

if (LicenseManager) {
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);
}

const GridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  flex-grow: 1;
  background-color: ${(props) => props.theme.palette.background.paper};

  .ag-cell.${HISTORY_NUMERIC_CLASS} {
    font-variant-numeric: tabular-nums;
  }
  ${questionDateFilterGridCss}
  /* a row opens that patient's MAIA history */
  .ag-row {
    cursor: pointer;
  }
  /* the totals row under the last row opens nothing */
  .ag-row-pinned {
    cursor: default;
    font-weight: 700;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  padding: 6px 10px;
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
`;

type Summed = Pick<
  PatientChatPatientSummary,
  | 'conversationCount'
  | 'questionCount'
  | 'totalTokensUsed'
  | 'totalDurationSeconds'
  | 'totalPromptTokens'
  | 'totalCachedTokens'
  | 'totalCandidatesTokens'
  | 'totalThoughtsTokens'
  | 'totalInputTokens'
  | 'totalOutputTokens'
  | 'questionsCostUsd'
  | 'cacheCostUsd'
  | 'totalEstimatedCostUsd'
>;

const SUMMED: (keyof Summed)[] = [
  'conversationCount',
  'questionCount',
  'totalTokensUsed',
  'totalDurationSeconds',
  'totalPromptTokens',
  'totalCachedTokens',
  'totalCandidatesTokens',
  'totalThoughtsTokens',
  'totalInputTokens',
  'totalOutputTokens',
  'questionsCostUsd',
  'cacheCostUsd',
  'totalEstimatedCostUsd',
];

/**
 * The bottom row: every number column summed over the rows the column filters
 * leave, so it always matches what is listed. A column no row has a value
 * for stays empty. Null (no row) when the list is empty.
 */
export const totalsRow = (
  rows: PatientChatPatientSummary[],
): PatientChatPatientSummary | null => {
  if (rows.length === 0) {
    return null;
  }
  const total = SUMMED.reduce<Partial<Summed>>((sums, key) => {
    const values = rows
      .map((row) => row[key])
      .filter((value): value is number => typeof value === 'number');
    return {
      ...sums,
      [key]: values.length ? values.reduce((a, b) => a + b, 0) : null,
    };
  }, {});
  return ({
    ...total,
    patientNumber: '',
    patientName: defaults.patientsTotal,
    firstQuestionAt: '',
    lastQuestionAt: '',
  } as unknown) as PatientChatPatientSummary;
};

/** The rows the column filters leave, in no particular order. */
const listedRows = (api: GridApi): PatientChatPatientSummary[] => {
  const rows: PatientChatPatientSummary[] = [];
  api.forEachNodeAfterFilter(({ data }) => {
    if (data) {
      rows.push(data as PatientChatPatientSummary);
    }
  });
  return rows;
};

/** Why the list is empty: the column filters left nothing, no chats in the dates, or no chats at all. */
export const NoPatientsOverlay = ({
  api,
  inDateRange,
}: {
  api?: GridApi;
  inDateRange?: boolean;
}) => {
  let message = defaults.noPatientsWithChats;
  if (api?.isAnyFilterPresent()) {
    message = defaults.noPatientsMatch;
  } else if (inDateRange) {
    message = defaults.noPatientsInRange;
  }
  return <span>{message}</span>;
};

type PatientListGridProps = {
  /** null while the first load is in flight (shows the loading overlay). */
  rows: PatientChatPatientSummary[] | null;
  error?: string | null;
  smallWidget?: boolean;
  siteTimezone?: string | null;
  /** The rows are for a date range (the empty message says so). */
  inDateRange?: boolean;
  /** Expanded: the date filter, framed over Conversations to the last token column. */
  dateFilter?: QuestionDateFilterState | null;
  /** The grid's api once ready, so the header can clear the filters. */
  onGridApi?: (api: GridApi) => void;
  /** Whether any column filter is set, after each filter change. */
  onFiltersChange?: (active: boolean) => void;
  /** A row was clicked: open that patient's MAIA history. */
  onOpenPatient?: (row: PatientChatPatientSummary) => void;
};

/**
 * MAIA Chats (cross-patient): one row per patient with MAIA chats, last
 * question first. Same grid stack as the per-patient history and Incidents.
 */
export const PatientListGrid = ({
  rows,
  error,
  smallWidget,
  siteTimezone,
  inDateRange,
  dateFilter,
  onGridApi,
  onFiltersChange,
  onOpenPatient,
}: PatientListGridProps) => {
  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const showCost = useCostColumnsShown();
  const rowHeight = gridTheme?.rowHeight || 27;
  // the totals of the listed rows, shown with "See tokens" (Mumms admins)
  const [total, setTotal] = useState<PatientChatPatientSummary | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  /**
   * A pinned row sits at the bottom of the grid's viewport. While the rows
   * fit, the grid takes their height (autoHeight), so the totals row comes
   * right under the last one; once they scroll, it stays in view at the
   * bottom.
   */
  const placeTotals = useCallback(
    (api: GridApi) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      if (!showCost) {
        api.setDomLayout('normal');
        return;
      }
      const header = wrapper.querySelector<HTMLElement>('.ag-header');
      const errorText = wrapper.querySelector<HTMLElement>('[role="alert"]');
      const needed =
        (header?.offsetHeight ?? 0) +
        (api.getDisplayedRowCount() + 1) * rowHeight;
      const available = wrapper.clientHeight - (errorText?.offsetHeight ?? 0);
      api.setDomLayout(needed < available ? 'autoHeight' : 'normal');
    },
    [showCost, rowHeight],
  );
  // AG Grid reads gridOptions once: its handlers reach the latest through this
  const placeTotalsRef = useRef(placeTotals);
  placeTotalsRef.current = placeTotals;

  // only whether there is a filter: its dates reach the header through context
  const hasDateFilter = Boolean(dateFilter);
  const columnDefs = useMemo(() => {
    const defs = getPatientListColumnDefs({
      smallWidget,
      siteTimezone,
      showCost,
    });
    return hasDateFilter
      ? withQuestionDateFilter(defs, 'conversationCount', LAST_TOKEN_COLUMN)
      : defs;
  }, [smallWidget, siteTimezone, showCost, hasDateFilter]);

  const gridOptions: GridOptions = useMemo(
    () => ({
      defaultColDef: {
        resizable: true,
        sortable: true,
      },
      suppressCellSelection: true,
      getRowNodeId: (data: PatientChatPatientSummary) => data.patientNumber,
      onGridReady: ({ api }: GridReadyEvent) => {
        api.sizeColumnsToFit();
        if (onGridApi) {
          onGridApi(api);
        }
      },
      onGridSizeChanged: ({ api }) => {
        api.sizeColumnsToFit();
        placeTotalsRef.current(api);
      },
      // "See tokens" adds or removes columns and the totals row: fit them again
      onDisplayedColumnsChanged: ({ api }) => {
        api.sizeColumnsToFit();
        placeTotalsRef.current(api);
      },
      // new rows, or the column filters changed what is listed
      onModelUpdated: ({ api }: ModelUpdatedEvent) => {
        setTotal(totalsRow(listedRows(api)));
        placeTotalsRef.current(api);
      },
      onRowClicked: ({ data, node }: RowClickedEvent) => {
        if (data && !node.rowPinned && onOpenPatient) {
          onOpenPatient(data as PatientChatPatientSummary);
        }
      },
      // AG Grid 25 only shows the no-rows overlay for empty rowData, not when
      // the filters leave nothing; show it for that case too.
      onFilterChanged: ({ api }: FilterChangedEvent) => {
        if (api.getDisplayedRowCount() === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }
        if (onFiltersChange) {
          onFiltersChange(api.isAnyFilterPresent());
        }
      },
    }),
    [onGridApi, onFiltersChange, onOpenPatient],
  );

  return (
    <GridWrapper data-testid="maia-patient-list-grid" ref={wrapperRef}>
      {error ? <ErrorText role="alert">{error}</ErrorText> : null}
      <QuestionDateFilterContext.Provider
        value={dateFilter ? { filter: dateFilter } : null}
      >
        <AgGridTableWrapper
          className={`${gridTheme.className} light`}
          name="maia-patient-list-table-wrapper"
        >
          <AgGridReact
            gridOptions={gridOptions}
            columnDefs={columnDefs}
            rowData={rows ?? undefined}
            headerHeight={gridTheme?.headerHeight || 27}
            groupHeaderHeight={
              hasDateFilter ? QUESTION_DATE_FILTER_HEIGHT : undefined
            }
            rowHeight={rowHeight}
            pinnedBottomRowData={showCost && total ? [total] : []}
            frameworkComponents={{ noPatientsOverlay: NoPatientsOverlay }}
            noRowsOverlayComponent="noPatientsOverlay"
            noRowsOverlayComponentParams={{ inDateRange }}
            overlayLoadingTemplate={`<span>${defaults.loadingConversations}</span>`}
          />
        </AgGridTableWrapper>
      </QuestionDateFilterContext.Provider>
    </GridWrapper>
  );
};

export default PatientListGrid;
